import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ChevronLeft, Heart, ShoppingCart, Star, ExternalLink, BookOpen, Edit, Sparkles, Archive, Minus, Plus, Settings, PlusCircle, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

// Importar de detailsService
import { 
  getGameById, 
  getGameBySlug, 
  normalizeSlug,
  getGameUrl
} from '../lib/game/detailsService';
// Importar de collectionService
import { 
  toggleOwned, 
  toggleWishlist, 
  togglePlayed,
  updatePlayCount 
} from '../lib/game/collectionService';
// Importar de ratingService
import { rateGame, rateGameComplexity } from '../lib/game/ratingService';

import { useAuth } from '../contexts/AuthContext';
// Importar tipos de um local centralizado (ajustar se [R-TYPE-01] usou outro caminho)
import type { Game, UserGame, UserProfile /* GameStats */, GameReview } from '../types'; 
import { supabase } from '../lib/supabase';

// Manter a importação do COMPONENTE
import { GameStats } from '../components/GameStats';
import CommunityService from '../lib/services/communityService';
import { FeedbackService } from '../lib/services/feedbackService';
import configService, { DEFAULT_AMAZON_AFFILIATE_URL } from '../lib/configService';
import SEO from '../components/SEO';
import GameSchema from '../components/GameSchema';
import OptimizedImage from '../components/OptimizedImage';
import GameReviewsSummary from '../components/GameReviewsSummary';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';

const GameDetailsPage: React.FC = () => {
  const { gameSlug } = useParams<{ gameSlug?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, isAdmin } = useAuth();
  const { theme } = useAppTheme();
  const [game, setGame] = useState<Game | null>(null);
  const [userGame, setUserGame] = useState<UserGame | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [wishlistUsers, setWishlistUsers] = useState<UserProfile[]>([]);
  const [loadingWishlistUsers, setLoadingWishlistUsers] = useState(false);
  const feedbackService = new FeedbackService();
  const [loadingComplexity, setLoadingComplexity] = useState(false);
  const [loadingRating, setLoadingRating] = useState(false);
  const [localRating, setLocalRating] = useState<number | null>(null);

  // Verificar se há uma mensagem de redirecionamento
  const redirectState = location.state as { redirected?: boolean, message?: string } | null;

  useEffect(() => {
    console.log('[GameDetailsPage] Fetch Effect triggered. gameSlug:', gameSlug, 'User ID:', user?.id);
    const fetchGameData = async () => {
      if (gameSlug) { 
        await loadGameDetailsBySlug(gameSlug);
      } 
    };

    if (gameSlug) { 
        fetchGameData();
    } else {
        console.warn('[GameDetailsPage] No gameSlug provided, skipping fetch.');
        setLoading(false); 
    }

  }, [gameSlug, user?.id]);

  // Redirecionar URLs apenas se vieram de slug
  useEffect(() => {
    // Usar gameSlug
    console.log('[GameDetailsPage] Redirect Effect triggered. gameSlug:', gameSlug, 'Game Name:', game?.name, 'Loading:', loading);
    // Se o jogo estiver carregado e tivermos um gameSlug
    if (gameSlug && game && !loading) {
      // Calcular o slug correto baseado no nome do jogo carregado
      const correctSlug = normalizeSlug(game.name);
      console.log('[GameDetailsPage] Comparing slugs. URL Slug:', gameSlug, 'Correct Slug:', correctSlug);
      
      // Apenas redirecionar se o slug estiver incorreto
      if (gameSlug !== correctSlug) {
        const correctUrl = `/games/${correctSlug}`;
        
        // Adicionar estado ao navigate para indicar que foi um redirecionamento
        navigate(correctUrl, { 
          replace: true,
          state: { 
            redirected: true, 
            originalUrl: `/games/${gameSlug}`, // Usar gameSlug aqui também
            message: `Jogo encontrado: "${game.name}" pelo termo semelhante "${gameSlug.replace(/-/g, ' ')}"`
          }
        });
      }
    }
  }, [game?.name, gameSlug, navigate]); // Depender de gameSlug

  // Carregar usuários que têm o jogo na wishlist
  useEffect(() => {
    const fetchWishlistUsers = async () => {
      if (game && game.id) {
        setLoadingWishlistUsers(true);
        try {
          const communityService = new CommunityService();
          const users = await communityService.getUsersByWishlistGame(game.id);
          setWishlistUsers(users);
        } catch (error) {
          console.error('Erro ao buscar usuários interessados no jogo:', error);
        } finally {
          setLoadingWishlistUsers(false);
        }
      }
    };

    fetchWishlistUsers();
  }, [game?.id]);

  // Helper para sanitizar UserGame (garantir play_count é number)
  const sanitizeUserGame = (data: any): UserGame | null => {
    if (!data) return null;
    // Assume que data tem a estrutura de UserGame vindo do DB (com play_count: number | null)
    return {
      ...data,
      play_count: data.play_count ?? 0, // Garante que play_count seja number
    } as UserGame;
  };

  // Função para buscar e sanitizar UserGame atualizado
  const fetchUpdatedUserGame = async (userId: string, gameId: string): Promise<UserGame | null> => {
    const { data, error } = await supabase
      .from('user_games')
      .select('*')
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .maybeSingle();
    if (error) {
      console.error("Erro ao buscar user game atualizado:", error);
      return null;
    }
    return sanitizeUserGame(data); // Sanitiza antes de retornar
  };

  const loadGameDetailsBySlug = async (gameSlug: string) => {
    console.log(`[GameDetailsPage] loadGameDetailsBySlug - Start. Slug: ${gameSlug}`);
    try {
      setLoading(true);
      setError(null);
      
      const gameData = await getGameBySlug(gameSlug, user?.id);
      console.log(`[GameDetailsPage] loadGameDetailsBySlug - getGameBySlug returned:`, gameData);
      
      setGame(gameData as Game); // gameData já vem com stats atualizados pela função?
      
      // Se o jogo tiver userGame (relacionamento), armazenar e SANITIZAR
      const rawUserGame = (gameData as any).user_game;
      setUserGame(sanitizeUserGame(rawUserGame)); 
      setLocalRating(rawUserGame?.rating ?? null); // Inicializa rating local
      console.log(`[GameDetailsPage] loadGameDetailsBySlug - UserGame set (sanitized).`);
      
    } catch (err) {
      console.error('[GameDetailsPage] loadGameDetailsBySlug - Error caught:', err);
      setError('Não foi possível carregar os detalhes do jogo. Tente novamente mais tarde.');
    } finally {
      console.log('[GameDetailsPage] loadGameDetailsBySlug - Finally block. Setting loading to false.');
      setLoading(false);
    }
  };

  const handleToggleWishlist = async (isWishlist: boolean) => {
    if (!user?.id || !game?.id) return;
    // Idealmente, adicionar setLoadingWishlist(true) aqui
    try {
      const updated = await toggleWishlist(user.id, game.id, isWishlist);
      setUserGame(sanitizeUserGame(updated)); // Sanitizar antes de setar estado
      
      // Forçar a atualização das estatísticas do jogo
      const { data: rpcStatsData, error } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!error && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar wishlist:', error);
      toast.error('Erro ao atualizar wishlist.');
    } finally {
      // setLoadingWishlist(false);
    }
  };

  const handleToggleOwned = async (isOwned: boolean) => {
    if (!user?.id || !game?.id) return;
    // setLoadingOwned(true)
    try {
      const updated = await toggleOwned(user.id, game.id, isOwned);
      setUserGame(sanitizeUserGame(updated)); // Sanitizar
      
      const { data: rpcStatsData, error } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!error && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar coleção:', error);
      toast.error('Erro ao atualizar coleção.');
    } finally {
      // setLoadingOwned(false)
    }
  };
  
  const handleTogglePlayed = async (isPlayed: boolean) => {
    if (!user?.id || !game?.id) return;
    // setLoadingPlayed(true)
    try {
      const updated = await togglePlayed(user.id, game.id, isPlayed);
      setUserGame(sanitizeUserGame(updated)); // Sanitizar
      
      const { data: rpcStatsData, error } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!error && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }
    } catch (error) {
      console.error('[GameDetailsPage] Erro ao marcar jogo como jogado:', error);
      toast.error('Erro ao marcar como jogado.');
    } finally {
      // setLoadingPlayed(false)
    }
  };
  
  const handleUpdatePlayCount = async (newPlayCount: number) => {
    if (!user?.id || !game?.id) return;
    // setLoadingPlayCount(true)
    try {
      const updated = await updatePlayCount(user.id, game.id, newPlayCount);
      setUserGame(sanitizeUserGame(updated)); // Sanitizar
      
      const { data: rpcStatsData, error } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!error && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }
    } catch (error) {
      console.error('Erro ao atualizar contagem de jogadas:', error);
      toast.error('Erro ao atualizar partidas.');
    } finally {
      // setLoadingPlayCount(false)
    }
  };

  // --- Refatorado --- 
  const handleRatingChange = async (newRating: number | null) => { // Nomeado handleRatingChange
    if (!user || !game) return;

    try {
      setLoadingRating(true);
      // Chamar rateGame do ratingService
      // rateGame já retorna UserGame | null, vamos sanitizar
      const resultUserGame = await rateGame(user.id, game.id, newRating ?? 0); 
      const updatedUserGameData = sanitizeUserGame(resultUserGame);

      if (updatedUserGameData) {
         setUserGame(updatedUserGameData); // Já está sanitizado
         setLocalRating(newRating); // Atualiza a UI imediatamente
      } else {
         console.warn("Não foi possível obter UserGame atualizado após rateGame");
         // Opcional: reverter localRating? ou buscar novamente?
         setLocalRating(userGame?.rating ?? null); // Reverte para o estado anterior
      }

      toast.success('Avaliação atualizada com sucesso!');

      // Atualizar stats globais (separado da atualização do userGame local)
      const { data: rpcStatsData, error: statsError } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!statsError && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }

    } catch (error: any) {
      toast.error(`Erro ao avaliar o jogo: ${error.message}`);
      setLocalRating(userGame?.rating ?? null); // Reverte em caso de erro
    } finally {
      setLoadingRating(false);
    }
  };

  // --- Refatorado --- 
  const handleComplexityChange = async (complexity: number | null) => { // Nomeado handleComplexityChange
    if (!user || !game) return;

    try {
      setLoadingComplexity(true);
      // Chamar a função correta do ratingService
      // rateGameComplexity retorna { success, userGameId }
      await rateGameComplexity(user.id, game.id, complexity);

      // Após a chamada, buscar o registro atualizado para atualizar o estado local
      const updatedUserGameData = await fetchUpdatedUserGame(user.id, game.id);
      if (updatedUserGameData) {
        setUserGame(updatedUserGameData); // Já está sanitizado
      } else {
         console.warn("Não foi possível encontrar UserGame atualizado após rateGameComplexity");
         // Não precisa reverter aqui, pois não há estado local de complexidade para UI
      }

      toast.success('Complexidade atualizada com sucesso!');
      
      // Atualizar stats globais
      const { data: rpcStatsData, error: statsError } = await supabase.rpc('get_game_stats', { p_game_id: game.id });
      if (!statsError && rpcStatsData && game) {
        setGame(prevGame => {
          if (!prevGame) return null;
          const newStats = {
            ...(prevGame.stats || {}),
            ...(rpcStatsData as any),
            _timestamp: new Date().getTime(),
          };
          return {
            ...prevGame,
            stats: newStats,
          };
        });
      }

    } catch (error: any) {
      toast.error(`Erro ao atualizar complexidade: ${error.message}`);
    } finally {
      setLoadingComplexity(false);
    }
  };

  const handleAmazonClick = async () => {
    let amazonUrl = null;
    let storeProductId = null;
    
    // Verificar se o jogo possui algum produto específico na Amazon
    if (game && game.store_products && game.store_products.length > 0) {
      // Procurar por produtos da Amazon
      const amazonProduct = game.store_products.find(
        product => product.store && product.store.name.toLowerCase().includes('amazon')
      );
      
      if (amazonProduct && amazonProduct.product_url) {
        amazonUrl = amazonProduct.product_url;
        storeProductId = amazonProduct.id;
      }
    }
    
    // Se não encontrou um produto específico na Amazon para este jogo,
    // usar o link de fallback diretamente
    if (!amazonUrl) {
      amazonUrl = DEFAULT_AMAZON_AFFILIATE_URL || 'https://amzn.to/4jcXJhu';
    }
    
    // Rastrear o clique se tivermos um produto específico
    if (storeProductId) {
      await feedbackService.trackAffiliateClick(storeProductId, user?.id);
    }
    
    // Abrir a URL em uma nova aba
    window.open(amazonUrl, '_blank');
  };

  // Função para formatar a descrição do jogo
  const formatDescription = (description: string): string => {
    if (!description) return '';
    
    // Substituir quebras de linha
    let formattedText = description.replace(/\n/g, '<br>');
    
    // Substituir texto entre ** por tags de negrito
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    return formattedText;
  };

  // Renderizar as estrelas de avaliação - usar handleRatingChange e localRating
  const renderRatingStars = () => {
    const ratingToDisplay = localRating; // Usa o estado local para UI imediata
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleRatingChange(star === ratingToDisplay ? null : star)} // Permite desmarcar clicando na mesma estrela
            className={`rounded-full p-1 transition-colors duration-150 ${ 
              ratingToDisplay && ratingToDisplay >= star 
                ? 'text-yellow-400' 
                : 'text-gray-300 hover:text-yellow-300' // Hover sutil
            }`}
            disabled={!user || loadingRating} // Desabilita durante o carregamento
            aria-label={`Avaliar com ${star} estrelas`}
          >
            <Star className="h-8 w-8" />
          </button>
        ))}
      </div>
    );
  };

  // Renderizar as estrelas de avaliação de complexidade - usar handleComplexityChange
  const renderComplexityStars = () => {
    const complexityToDisplay = userGame?.complexity_rating; // Usa o estado atual do userGame
    
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            onClick={() => handleComplexityChange(star === complexityToDisplay ? null : star)} // Permite desmarcar
            className={`rounded-full p-1 transition-colors duration-150 ${ 
              complexityToDisplay && complexityToDisplay >= star 
                ? 'text-teal-400' 
                : 'text-gray-300 hover:text-teal-300' // Hover sutil
            }`}
            disabled={!user || loadingComplexity} // Desabilita durante o carregamento
            aria-label={`Avaliar complexidade com ${star}`}
          >
            <Settings className="h-8 w-8" />
          </button>
        ))}
      </div>
    );
  };

  // Função para obter uma URL de imagem válida ou um fallback
  const getValidImageUrl = (url: string | null) => {
    if (!url) return '/placeholder-game.jpg'; // Imagem local de fallback
    
    // Se for uma URL externa, verificar se é HTTPS
    if (url.startsWith('http')) {
      // Forçar HTTPS
      return url.replace(/^http:/, 'https:');
    }
    
    return url;
  };

  // Adicione uma seção para exibir e atualizar a contagem de jogadas na interface
  // Na seção onde estão os botões de ações do usuário
  
  const renderPlayCountSection = () => {
    if (!userGame) return null;
    
    const playCount = userGame.play_count || 0;
    const isPlayed = playCount > 0;
    
    if (!isPlayed) return null;
    
    return (
      <div className={`mt-4 border-t pt-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
        <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Contagem de Partidas</h3>
        <div className={`flex items-center rounded-lg inline-flex ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
          {/* <button 
            onClick={() => handleUpdatePlayCount(Math.max(1, playCount - 1))}
            className="p-2 text-gray-600 hover:text-indigo-600"
            disabled={playCount <= 1}
          >
            <Minus className="h-4 w-4" />
          </button> */}
          
          <div className={`px-3 py-1 font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
            {playCount} {playCount === 1 ? 'Partida' : 'Partidas'}
          </div>
          
          {/* <button 
            onClick={() => handleUpdatePlayCount(playCount + 1)}
            className="p-2 text-gray-600 hover:text-indigo-600"
          >
            <Plus className="h-4 w-4" />
          </button> */}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`container mx-auto max-w-6xl py-8 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="text-center py-12">
          <Loader2 className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 mx-auto ${theme === 'dark' ? 'text-indigo-400 border-indigo-400' : 'text-indigo-500 border-indigo-500'}`} />
          <p className={`mt-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Carregando detalhes do jogo...</p>
        </div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className={`container mx-auto max-w-6xl py-8 ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
        <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-red-50'}`}>
          <h2 className={`text-xl font-semibold mb-2 ${theme === 'dark' ? 'text-red-400' : 'text-red-700'}`}>
            {error || 'Jogo não encontrado'}
          </h2>
          <p className={`mb-6 ${theme === 'dark' ? 'text-red-300' : 'text-red-600'}`}>
            Não foi possível encontrar o jogo solicitado
          </p>
          <Link
            to="/search"
            className={`px-4 py-2 rounded-lg transition ${theme === 'dark' ? 'bg-indigo-500 text-white hover:bg-indigo-600' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
          >
            Voltar para a Busca
          </Link>
        </div>
      </div>
    );
  }

  const isOwned = userGame?.owned ?? false;
  const isWishlist = userGame?.wishlist ?? false;
  const isPlayed = userGame ? userGame.play_count > 0 : false; // Usa userGame sanitizado
  const secureImageUrl = getValidImageUrl(game.image_url);

  return (
    <>
      {game && (
        <>
          <SEO 
            title={game.name}
            description={game.description?.substring(0, 160) || `${game.name} - Informações, regras, avaliações e onde comprar.`}
            image={game.image_url || undefined} // Passar undefined se null
            url={`https://boardverso.com.br/games/${game.slug || game.id}`}
          />
          <GameSchema game={game} />
          
          <div className={`container mx-auto px-4 py-8 ${theme === 'dark' ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'}`}>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <Loader2 className={`animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 ${theme === 'dark' ? 'text-indigo-400 border-indigo-400' : 'text-indigo-500 border-indigo-500'}`} />
              </div>
            ) : error ? (
              <div className={`border px-4 py-3 rounded ${theme === 'dark' ? 'bg-red-900/20 border-red-700 text-red-300' : 'bg-red-100 border-red-400 text-red-700'}`}>
                <p>{error}</p>
              </div>
            ) : game ? (
              <>
                {/* Mostrar mensagem de redirecionamento se existir */}
                {redirectState?.message && (
                  <div className={`alert alert-info mb-4 px-4 py-3 border-l-4 rounded shadow-sm ${theme === 'dark' ? 'border-blue-400 bg-blue-900/30 text-blue-300' : 'border-blue-500 bg-blue-50 text-blue-700'}`} style={{ 
                    animation: 'fadeIn 0.5s ease-in-out'
                  }}>
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      <p>{redirectState.message}</p>
                    </div>
                  </div>
                )}
              
                <div className="flex flex-col md:flex-row gap-8 mb-8">
                  <div className="md:w-1/3">
                    <div className={`relative pb-[100%] rounded-lg overflow-hidden shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <OptimizedImage 
                        src={game.image_url || 'https://via.placeholder.com/500x500?text=Sem+Imagem'} 
                        alt={game.name}
                        className="absolute inset-0 w-full h-full object-contain"
                      />
                    </div>
                    
                    {/* Ações do usuário */}
                    <div className="mt-6 flex flex-wrap gap-3">
                      <button
                        onClick={() => handleToggleOwned(!isOwned)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          isOwned
                            ? (theme === 'dark' ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700') 
                            : (theme === 'dark' ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300')
                        }`}
                      >
                        <ShoppingCart size={18} />
                        {isOwned ? 'Na Coleção' : 'Adicionar à Coleção'}
                      </button>
                      
                      <button
                        onClick={() => handleToggleWishlist(!isWishlist)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          isWishlist
                            ? (theme === 'dark' ? 'bg-purple-500 text-white hover:bg-purple-600' : 'bg-purple-600 text-white hover:bg-purple-700') 
                            : (theme === 'dark' ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300')
                        }`}
                      >
                        <Heart size={18} />
                        {isWishlist ? 'Na Wishlist' : 'Adicionar à Wishlist'}
                      </button>
                      
                      <button
                        onClick={() => handleTogglePlayed(!isPlayed)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                          isPlayed
                            ? (theme === 'dark' ? 'bg-green-500 text-white hover:bg-green-600' : 'bg-green-600 text-white hover:bg-green-700')
                            : (theme === 'dark' ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300')
                        }`}
                      >
                        <PlusCircle size={18} />
                        {isPlayed ? 'Jogado' : 'Marcar como Jogado'}
                      </button>
                    </div>
                    
                    {/* Botão de compra - destacado em seção separada */}
                    <div className={`mt-6 border-t pt-4 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                      <Link
                        to={`/game-prices/slug/${game.slug}`}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 mb-3 rounded-md text-white transition-colors font-medium shadow-sm ${theme === 'dark' ? 'bg-green-600 hover:bg-green-700' : 'bg-green-600 hover:bg-green-700'}`}
                      >
                        <DollarSign size={20} />
                        Ver preços em todas as lojas
                      </Link>
                      
                      <button
                        onClick={handleAmazonClick}
                        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-md text-white transition-colors font-medium text-lg shadow-sm ${theme === 'dark' ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-yellow-500 hover:bg-yellow-600'}`}
                      >
                        <ShoppingCart size={20} />
                        Comprar na Amazon
                      </button>
                      <p className={`text-xs mt-2 text-center ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        Compre online com um clique através do nosso link afiliado
                      </p>
                      <div className={`mt-3 p-3 rounded-md border ${theme === 'dark' ? 'bg-blue-900/30 border-blue-700' : 'bg-blue-50 border-blue-100'}`}>
                        <p className={`text-sm ${theme === 'dark' ? 'text-blue-300' : 'text-blue-800'}`}>
                          <span className="font-medium">Importante:</span> Os links específicos para cada jogo ainda não estão disponíveis, mas acessando a Amazon através do nosso link e realizando qualquer compra, você ajuda a manter o site no ar. Agradecemos seu apoio! 💙
                        </p>
                      </div>
                    </div>
                    
                    {/* Só mostra seção se JÁ jogou */} 
                    {isPlayed && renderPlayCountSection()}
                    
                    {/* Avaliação (Estrelas) */} 
                    {user && (
                      <div className="mt-6">
                        <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Sua Avaliação</h3>
                        {renderRatingStars()} {/* Usa a função refatorada */} 
                        <p className={`text-sm mt-2 italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="flex items-center">
                            <Sparkles className="h-4 w-4 mr-1" />
                            Avaliações ≥ 3 ajudam a recomendar jogos similares e &lt; 3 evitam recomendações parecidas.
                          </span>
                        </p>
                      </div>
                    )}
                    
                    {/* Avaliação de Complexidade */} 
                    {user && (
                      <div className="mt-6">
                        <h3 className={`text-lg font-semibold mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Sua Avaliação de Complexidade</h3>
                        {renderComplexityStars()} {/* Usa a função refatorada */} 
                        <p className={`text-sm mt-2 italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          <span className="flex items-center">
                            <Sparkles className="h-4 w-4 mr-1" />
                            Avalie a complexidade de 1 (muito simples) a 5 (muito complexo).
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="md:w-2/3">
                    <div className="flex items-center justify-between">
                      <h1 className={`text-3xl font-bold mb-2 ${theme === 'dark' ? 'text-gray-50' : 'text-gray-900'}`}>{game.name}</h1>
                      
                      {/* Botão de edição - apenas para admins */}
                      {isAdmin && (
                        <Link 
                          to={`/admin/edit-game/${game.id}`}
                          className={`flex items-center gap-2 px-4 py-2 rounded-md text-white transition-colors ${theme === 'dark' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-amber-500 hover:bg-amber-600'}`}
                        >
                          <Edit size={18} />
                          Editar Jogo
                        </Link>
                      )}
                    </div>
                    
                    {/* Detalhes básicos */}
                    <div className="flex flex-wrap gap-y-3 gap-x-6 mb-6">
                      {game.year && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Ano: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{game.year}</span>
                        </div>
                      )}
                      
                      {game.ano_nacional && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Lançamento no Brasil: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{game.ano_nacional}</span>
                        </div>
                      )}
                      
                      {(game.min_players || game.max_players) && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Jogadores: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>
                            {game.min_players === game.max_players 
                              ? game.min_players 
                              : `${game.min_players}-${game.max_players}`}
                          </span>
                        </div>
                      )}
                      
                      {game.play_time && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Tempo de Jogo: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{game.play_time} min</span>
                        </div>
                      )}
                      
                      {game.min_age && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Idade: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{game.min_age}+ anos</span>
                        </div>
                      )}
                      
                      {game.complexity && (
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Complexidade: </span>
                          <span className={`font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>{game.complexity.toFixed(1)}/5</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Descrição */}
                    {game.description && (
                      <div className="mb-6">
                        <h2 className={`text-xl font-semibold mb-3 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Descrição</h2>
                        <div 
                          className={`prose max-w-none ${theme === 'dark' ? 'prose-invert text-gray-300' : 'prose-indigo text-gray-700'}`}
                          dangerouslySetInnerHTML={{ __html: formatDescription(game.description) }}
                        />
                      </div>
                    )}
                    
                    {/* Designers, Artistas, Editoras */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-4 gap-x-8 mb-6">
                      <div>
                        <h3 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Designers</h3>
                        {Array.isArray(game.designers) && game.designers.length > 0 ? (
                          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{game.designers.join(', ')}</p>
                        ) : (
                          <p className={`italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Informação não disponível</p>
                        )}
                      </div>
                      
                      <div>
                        <h3 className={`font-semibold mb-1 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Artistas</h3>
                        {Array.isArray(game.artists) && game.artists.length > 0 ? (
                          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{game.artists.join(', ')}</p>
                        ) : (
                          <p className={`italic ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Informação não disponível</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Categorias e Mecânicas */}
                    <div className="mb-6">
                      {game.categories && game.categories.length > 0 && (
                        <div className="mb-4">
                          <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Categorias</h3>
                          <div className="flex flex-wrap gap-2">
                            {game.categories.map((category, index) => (
                              <span 
                                key={index} 
                                className={`px-3 py-1 rounded-full text-sm ${theme === 'dark' ? 'bg-indigo-700 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`}
                              >
                                {category}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {game.mechanics && game.mechanics.length > 0 && (
                        <div className="mb-4">
                          <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Mecânicas</h3>
                          <div className="flex flex-wrap gap-2">
                            {game.mechanics.map((mechanic, index) => (
                              <span 
                                key={index} 
                                className={`px-3 py-1 rounded-full text-sm ${theme === 'dark' ? 'bg-teal-700 text-teal-200' : 'bg-teal-100 text-teal-800'}`}
                              >
                                {mechanic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {game.themes && game.themes.length > 0 && (
                        <div>
                          <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Temas</h3>
                          <div className="flex flex-wrap gap-2">
                            {game.themes.map((themeItem, index) => ( // Renomeado para themeItem para evitar conflito
                              <span 
                                key={index} 
                                className={`px-3 py-1 rounded-full text-sm ${theme === 'dark' ? 'bg-amber-700 text-amber-200' : 'bg-amber-100 text-amber-800'}`}
                              >
                                {themeItem}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Galeria de imagens */}
                {/* Comentado temporariamente - a propriedade images não existe na interface Game
                {game.images && game.images.length > 0 && (
                  <div className="mb-8">
                    <h2 className="text-xl font-bold mb-4">Imagens</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {game.images.map((image, index) => (
                        <div key={index} className="rounded-lg overflow-hidden">
                          <img 
                            src={image} 
                            alt={`${game.name} - imagem ${index + 1}`} 
                            className="w-full h-48 object-cover hover:opacity-90 transition-opacity cursor-pointer"
                            onClick={() => {}} // Corrigido: Mantém a função vazia
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                */}
                
                {/* Estatísticas da Comunidade */}
                <GameStats game={game} />
                
                {/* Avaliações dos Usuários */}
                <GameReviewsSummary gameId={game.id} gameName={game.name} gameSlug={game.slug || undefined} /> {/* Passar undefined se null */}
                
                {/* Usuários interessados no jogo */}
                <div className="mt-8 mb-6">
                  <h2 className={`text-xl font-semibold mb-4 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>Pessoas interessadas neste jogo</h2>
                  {loadingWishlistUsers ? (
                    <div className="flex justify-center items-center py-4">
                      <Loader2 className={`animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 ${theme === 'dark' ? 'text-indigo-400 border-indigo-400' : 'text-indigo-500 border-indigo-500'}`} />
                    </div>
                  ) : wishlistUsers.length > 0 ? (
                    <div className={`rounded-lg shadow-md p-4 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {wishlistUsers.map(userProfile => ( // Renomeado para userProfile para evitar conflito
                          <Link 
                            key={userProfile.id} 
                            to={`/u/${userProfile.username}`} 
                            className={`flex items-center space-x-4 p-3 rounded-lg border transition-colors ${theme === 'dark' ? 'border-gray-700 hover:bg-gray-700/70' : 'border-gray-100 hover:bg-gray-50'}`}
                          >
                            <div className="flex-shrink-0">
                              <img 
                                src={userProfile.avatar_url || `https://ui-avatars.com/api/?name=${userProfile.username || userProfile.full_name || 'Usuário'}&background=random`} 
                                alt={userProfile.username || userProfile.full_name || 'Usuário'} 
                                className="w-10 h-10 rounded-full"
                              />
                            </div>
                            <div>
                              <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{userProfile.username || userProfile.full_name || 'Usuário'}</p>
                              {userProfile.bio && <p className={`text-sm truncate max-w-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{userProfile.bio}</p>}
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Nenhum usuário tem este jogo na wishlist ainda.</p>
                  )}
                </div>
                
              </>
            ) : (
              <div className={`border px-4 py-3 rounded ${theme === 'dark' ? 'bg-yellow-900/20 border-yellow-700 text-yellow-300' : 'bg-yellow-100 border-yellow-400 text-yellow-700'}`}>
                <p>Jogo não encontrado</p>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default GameDetailsPage; 